import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from "react-redux";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { getActionTemplate } from "../../../actions/actionTemplateActions";
import { getGroupTemplate } from "../../../actions/groupTemplateActions";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArgoTemplateListDividerHook from "../../common/ArgoTemplateListDividerHook";
import * as expander from "../../../utilities/expander";
import Typography from "@mui/material/Typography";
import * as TEMPLATE from "../../../constants/templateConstants";
import ArgoExpandCollapseButtonHook from "../../common/ArgoExpandCollapseButtonHook";
import * as metadataUtils from "../../../utilities/metadata";
import Box from "@mui/material/Box";


const AnswerViewerHook = (props) => {
    const {
        answerOptionTemplates,
        parentsLabel,
        index
    } = props;

    const [state, setState] = useState({
        expander: {},
        expandAll: true,
        expanderPreset: false
    });

    const dispatch = useDispatch();
    const actionTemplate = useSelector(state => state.actionTemplate);
    const actionTypeList = useSelector(state => state.actionTypeList);
    const groupTemplate = useSelector(state => state.groupTemplate);

    useEffect(() => {
        for (const template of answerOptionTemplates) {
            for (const key of template.importActionKeys) {
                dispatch(getActionTemplate(key));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Initialize expander state for answers
    useEffect(() => {
        if (!state.expanderPreset && answerOptionTemplates) {
            const answerKeys = answerOptionTemplates.map((_, answerIndex) => answerIndex);

            setState(prevState => ({
                ...prevState,
                expanderPreset: true,
                expander: expander.presetExpander(answerKeys, answerOptionTemplates, prevState.expander)
            }));
        }
    }, [answerOptionTemplates, state.expanderPreset]);

    const setStateCallback = (stateObj) => {
        setState(prevState => ({
            ...prevState,
            ...stateObj
        }));
    };

    const listActions = (answerOption) => {
        let selectedActions = {};

        actionTypeList.forEach((type) => {
            selectedActions[type] = [];
        });

        answerOption.importActionKeys.forEach((templateKey) => {
            if (!actionTemplate[templateKey]) {
                return <div key={templateKey}>{templateKey} not found</div>;
            }

            let type = (actionTemplate[templateKey].type) ? actionTemplate[templateKey].type : "other";
            if (selectedActions[type]) {
                selectedActions[type].push(actionTemplate[templateKey]);
            }
            else {
                return <div key={type}>{type} action type is missing</div>;
            }
        });

        return (
            <div>
                <div>Actions:</div>
                {Object.keys(selectedActions).map((type) => {
                    if (selectedActions[type].length > 0) {
                        return (
                            <div key={type + index} style={{width: "100%"}}>
                                <div key={type + "action_label_" + index} style={{paddingLeft: "15px", textTransform: "capitalize"}}>{type + ":"}</div>
                                {selectedActions[type].map((action) => {
                                    return (
                                        <div key={action.templateKey} style={{paddingLeft: "30px", color: "rgb(158,158,158)"}}>
                                            {action.name}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    }
                    return null;
                })}
                <div>&nbsp;</div>
            </div>
        );
    };

    const listGroups = (answerOption) => {
        let html = <div><div>Show Group:</div><div>&nbsp;</div></div>;
        let key = answerOption.importGroupKeys[0];  // Note we only allow 1 show group at this time

        if (key !== undefined) {
            if (groupTemplate[key]) {
                let group = groupTemplate[key];
                html = <div id={"AnswerViewer-answer-" + answerOption.id + "showgroup-container-div"}>
                    <div>Show Group:</div>
                    <div key={key} style={{paddingLeft: "15px", color: "rgb(158,158,158)"}}>
                        {group.name + (group.nameQualifier === null ? "" : " Qualifier: " + group.nameQualifier)}
                    </div>
                    <div>&nbsp;</div>
                </div>;
            } else {
                // Group not loaded, so get group from key
                dispatch(getGroupTemplate(key));
            }
        }

        return html;
    };

    return (
        Object.keys(state.expander).length > 0 ?
            <div style={{flexGrow: "2", paddingBottom: "32px"}}>
                <div style={{minHeight: "min-content"}}>
                    {/*// ===========================================================================================================*/}
                    {/*// ANSWERS LIST DIVIDER */}
                    {/*// ===========================================================================================================*/}

                    <ArgoTemplateListDividerHook
                        fileName="AnswerViewer"
                        parentsLabel={parentsLabel}
                        childLabel={["answer","answers"]}
                        arrayLength={answerOptionTemplates.length}
                    />

                    {/*// ===========================================================================================================*/}
                    {/*// ANSWERS EXPAND/COLLAPSE ALL BUTTON */}
                    {/*// ===========================================================================================================*/}

                    <div id="AnswerViewer-Answer-ExpandCollapseButton-container-div1" style={{display: "flex", flexDirection: "row", alignItems: "center", width: "100%"}}>
                        <div id="AnswerViewer-Answer-ExpandCollapseButton-container-div2"
                             style={{display: "flex", marginLeft: "auto"}}>
                            <ArgoExpandCollapseButtonHook
                                fileName="AnswerViewer"
                                expandAll={state.expandAll}
                                onClick={() => expander.expandCollapseAll(
                                    TEMPLATE.TYPE.ANSWER, 
                                    state.expander, 
                                    state.expandAll, 
                                    Object.keys(state.expander), 
                                    false, 
                                    setStateCallback
                                )}
                            />
                        </div>
                    </div>

                    <div>&nbsp;</div>

                    {/*// ===========================================================================================================*/}
                    {/*// ANSWERS LIST: Note: Question Types RADIO and CHECKBOX, and LIKERT Groups */}
                    {/*// ===========================================================================================================*/}

                    <div id={"AnswerViewer-answers-container-div-" + answerOptionTemplates.id}>
                        {answerOptionTemplates.map((answerOption, index) => (
                            <div key={"AnswerViewer-answer-container-div-" + answerOption.id} id={"AnswerViewer-answer-container-div-" + answerOption.id} style={{width: "100%"}}>
                                <Accordion id={"AnswerViewer-Accordion-" + index}
                                            variant="outlined"
                                            disableGutters={true}
                                            defaultExpanded={true}
                                            expanded={state.expander[index]?.open ?? false}
                                            onChange={() => expander.expandCollapseSelected(state.expander, index, setStateCallback)}
                                            sx={{width: "100%", minWidth: 480}}
                                >
                                    <AccordionSummary
                                        id={"AnswerViewer-AccordionSummary-" + index}
                                        expandIcon={<ExpandMoreIcon style={{color: expander.ICON_COLOR}}/>}
                                        aria-controls="panel1-content"
                                        sx={{
                                            height: "30px",
                                            backgroundColor: expander.ACCORDION_BG_COLOR,
                                            "&:hover": {backgroundColor: expander.ACCORDION_BG_COLOR_HOVER},
                                            "& .MuiAccordionSummary-content": {
                                                alignItems: "center",
                                                display: "flex",
                                                gap: 1, // small spacing between groups
                                                minWidth: 0,
                                            }
                                        }}
                                    >
                                        <Box style={{display: "flex", alignItems: "center", minWidth: 118, flex: 1}}>
                                            <Typography variant="h6" component="div" style={{flexShrink: 0, paddingLeft: 5, minWidth: 118, color: expander.TEXT_COLOR_SECONDARY}}>Answer Text:</Typography>
                                            <Typography variant="h6" component="div"
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            paddingLeft: 10,
                                                            color: expander.TEXT_COLOR_PRIMARY,
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis"
                                                        }}>
                                                {answerOption.optionText}
                                            </Typography>
                                        </Box>

                                        <Box style={{display: "flex", alignItems: "center",  minWidth: 75}}>
                                            <Typography variant="h6" component="div" style={{flexShrink: 0, paddingLeft: 10, minWidth: 75, color: expander.TEXT_COLOR_SECONDARY}}>Actions:</Typography>
                                            <Typography variant="subtitle1" component="div"
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            paddingLeft: 10,
                                                            color: expander.ICON_COLOR,
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis"
                                                        }}>
                                                {"(" + answerOption.importActionKeys.length + ")"}
                                            </Typography>
                                        </Box>

                                        <Box style={{display: "flex", alignItems: "center", minWidth: 116}}>
                                            <Typography variant="h6" component="div" style={{flexShrink: 0, paddingLeft: 10, minWidth: 116, color: expander.TEXT_COLOR_SECONDARY}}>Show Group:</Typography>
                                            <Typography variant="subtitle1" component="div"
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            paddingLeft: 10,
                                                            color: expander.ICON_COLOR,
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            paddingRight: metadataUtils.countNumberOfVisibleMetadataEntries(answerOption.metadata) > 0 ? 0 : 8
                                                        }}>
                                                {"(" + (answerOption.importGroupKeys.length ? "Yes" : "No") + ")"}
                                            </Typography>
                                        </Box>

                                        {metadataUtils.countNumberOfVisibleMetadataEntries(answerOption.metadata) > 0 ?
                                            <Box style={{display: "flex", alignItems: "center", minWidth: 0}}>
                                                <Typography variant="h6" component="div" style={{flexShrink: 0, paddingLeft: 10,  color: expander.TEXT_COLOR_SECONDARY}}>Metadata:</Typography>
                                                <Typography variant="subtitle1" component="div"
                                                            style={{
                                                                flex: 1,
                                                                minWidth: 0,
                                                                paddingLeft: 10,
                                                                color: expander.ICON_COLOR,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                paddingRight: 8
                                                            }}>
                                                    {"(" + metadataUtils.countNumberOfVisibleMetadataEntries(answerOption.metadata) + ")"}
                                                </Typography>
                                            </Box> : ""
                                        }
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {listActions(answerOption)}
                                        {listGroups(answerOption)}
                                        {metadataUtils.listMetadata(answerOption, "", "rgba(0, 0, 0, 0.87)")}
                                    </AccordionDetails>
                                </Accordion>

                                {state.expander[index]?.open ? <div id={"spacer-div-" + index}>&nbsp;</div> : ""}
                            </div>
                        ))}
                    </div>

                    {state.expanded ? <div id={"spacer-div-" + index}>&nbsp;</div> : ""}
                </div>
            </div>
            : ""
    );
};

AnswerViewerHook.defaultProps = {
    parentsLabel: "Question"
};

AnswerViewerHook.propTypes = {
    answerOptionTemplates: PropTypes.array,
    parentsLabel: PropTypes.string,
    index: PropTypes.number
};

export default AnswerViewerHook;